"use client";

import React from "react";
import Image from "next/image";
import type { GoogleDriveFile } from "./types";
import { getFileTypeIcon } from "./utils";
import { UnavailablePreview } from "./UnavailablePreview";

interface PreviewContentProps {
  file: GoogleDriveFile;
  previewError: string | null;
  canDownload: boolean;
  isDownloading: boolean;
  onPreviewError: () => void;
  onOpenInDrive: () => void;
  onDownload: () => void;
}

export function PreviewContent({
  file,
  previewError,
  canDownload,
  isDownloading,
  onPreviewError,
  onOpenInDrive,
  onDownload,
}: PreviewContentProps) {
  if (previewError) {
    return (
      <UnavailablePreview
        warningStyle
        title="Preview Not Available"
        description={previewError}
        canDownload={canDownload}
        onOpenInDrive={onOpenInDrive}
        openInDriveOnly
      />
    );
  }

  if (file.isGoogleDoc && file.webViewLink) {
    return (
      <div className="w-full h-full min-h-[600px] border rounded-lg overflow-hidden">
        <iframe
          src={`${file.webViewLink}&embedded=true`}
          className="w-full h-full min-h-[600px]"
          title={`Preview of ${file.name}`}
          onError={onPreviewError}
          sandbox="allow-same-origin allow-scripts allow-forms"
        />
      </div>
    );
  }

  if (file.mimeType.startsWith("image/")) {
    return (
      <div className="flex justify-center p-4">
        <Image
          src={file.thumbnailLink || file.webViewLink || "/placeholder.svg"}
          alt={file.name}
          width={800}
          height={600}
          className="max-w-full max-h-[600px] object-contain rounded-lg shadow-lg"
          onError={onPreviewError}
        />
      </div>
    );
  }

  if (file.mimeType === "application/pdf" && file.webViewLink) {
    return (
      <div className="w-full h-full min-h-[600px] border rounded-lg overflow-hidden">
        <iframe
          src={`${file.webViewLink}&embedded=true`}
          className="w-full h-full min-h-[600px]"
          title={`Preview of ${file.name}`}
          onError={onPreviewError}
        />
      </div>
    );
  }

  return (
    <UnavailablePreview
      title="Preview Not Available"
      description="This file type cannot be previewed directly. You can download it or open it in Google Drive."
      fileTypeIcon={getFileTypeIcon(file.mimeType)}
      canDownload={canDownload}
      isDownloading={isDownloading}
      onOpenInDrive={onOpenInDrive}
      onDownload={onDownload}
    />
  );
}
